import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { onboardingTourApi } from "./onboardingTour.api";
import { OnboardingTourState, TourStepId } from "./schema";

export interface OnboardingTourSliceState {
	/** Last known server-side tour state (hydrated from every endpoint response). */
	state: OnboardingTourState | null;
	/** Whether the full-screen tour modal is open. */
	isOpen: boolean;
	/** Active pipeline stage (0..2) inside the HOW_IT_WORKS step, driven by the footer. */
	activeHowItWorksStage: number;
	skipModalOpen: boolean;
	finishModalOpen: boolean;
	/**
	 * True while the CONNECT step's Unipile connect flow (MUI modals, z-index ~1000)
	 * is open — the tour overlay lowers its z-index below them for that time.
	 */
	connectFlowOpen: boolean;
	/**
	 * KNOWLEDGE step: the user left the tour for the Knowledge DB page at least
	 * once — the step body switches to its "all set" state. Survives closeTour
	 * (the whole point is returning after the round-trip).
	 */
	kbVisited: boolean;
	/** KNOWLEDGE step: the user saved/approved something on the Knowledge DB page during the tour visit. */
	kbEdited: boolean;
	/**
	 * True while the KNOWLEDGE step has handed the user off to the real Knowledge
	 * DB page (`?tour=1`). If they leave that page via the left nav instead of the
	 * return bar, the tour reopens the configuration modal (PREC-3745).
	 */
	kbHandoffActive?: boolean;
	/** Whether the wait-or-explore nudge modal (§5.3) is showing. */
	nudgeOpen?: boolean;
	/** Whether the lead-list tour overlay (§8) is open. */
	listTourOpen?: boolean;
}

const initialState: OnboardingTourSliceState = {
	state: null,
	isOpen: false,
	activeHowItWorksStage: 0,
	skipModalOpen: false,
	finishModalOpen: false,
	connectFlowOpen: false,
	kbVisited: false,
	kbEdited: false,
	kbHandoffActive: false,
	nudgeOpen: false,
	listTourOpen: false,
};

const hydrate = (state: OnboardingTourSliceState, payload: OnboardingTourState) => {
	state.state = payload;
};

const close = (state: OnboardingTourSliceState) => {
	state.isOpen = false;
	state.skipModalOpen = false;
	state.finishModalOpen = false;
	state.connectFlowOpen = false;
	state.activeHowItWorksStage = 0;
};

export const onboardingTourSlice = createSlice({
	name: "onboardingTour",
	initialState,
	reducers: {
		openTour: (state, { payload }: PayloadAction<TourStepId | undefined>) => {
			state.isOpen = true;
			// Reopening the modal (return bar or nav-away recovery) ends the handoff.
			state.kbHandoffActive = false;

			if (payload && state.state) {
				state.state.currentStep = payload;
			}
		},
		closeTour: close,
		setSkipModalOpen: (state, { payload }: PayloadAction<boolean>) => {
			state.skipModalOpen = payload;
		},
		setFinishModalOpen: (state, { payload }: PayloadAction<boolean>) => {
			state.finishModalOpen = payload;
		},
		setActiveHowItWorksStage: (state, { payload }: PayloadAction<number>) => {
			state.activeHowItWorksStage = payload;
		},
		setConnectFlowOpen: (state, { payload }: PayloadAction<boolean>) => {
			state.connectFlowOpen = payload;
		},
		setKbVisited: (state, { payload }: PayloadAction<boolean>) => {
			state.kbVisited = payload;
		},
		setKbEdited: (state, { payload }: PayloadAction<boolean>) => {
			state.kbEdited = payload;
		},
		setKbHandoffActive: (state, { payload }: PayloadAction<boolean>) => {
			state.kbHandoffActive = payload;
		},
		setNudgeOpen: (state, { payload }: PayloadAction<boolean>) => {
			state.nudgeOpen = payload;
		},
		openListTour: (state) => {
			state.listTourOpen = true;
			state.nudgeOpen = false;
			close(state);
		},
		closeListTour: (state) => {
			state.listTourOpen = false;
		},
		/** Optimistic step change — the PATCH response re-hydrates afterwards. */
		setCurrentStepLocal: (state, { payload }: PayloadAction<TourStepId>) => {
			if (state.state) {
				state.state.currentStep = payload;
			}
		},
		/** Optimistic completed-steps union — the PATCH response re-hydrates afterwards. */
		markStepCompletedLocal: (state, { payload }: PayloadAction<TourStepId>) => {
			if (state.state && !state.state.completedSteps.includes(payload)) {
				state.state.completedSteps.push(payload);
			}
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			onboardingTourApi.endpoints.getOnboardingTour.matchFulfilled,
			(state, { payload }) => hydrate(state, payload)
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.updateOnboardingTourProgress.matchFulfilled,
			(state, { payload }) => hydrate(state, payload)
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.skipOnboardingTour.matchFulfilled,
			(state, { payload }) => {
				hydrate(state, payload);
				close(state);
			}
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.exploreOnboardingTour.matchFulfilled,
			(state, { payload }) => {
				hydrate(state, payload);
				state.listTourOpen = true;
				state.nudgeOpen = false;
				close(state);
			}
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.markNudgeShown.matchFulfilled,
			(state, { payload }) => hydrate(state, payload)
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.updateListTour.matchFulfilled,
			(state, { payload }) => hydrate(state, payload)
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.reachOnboardingTourCheck.matchFulfilled,
			// Only the waiting-list count: this answer races the PATCH that moved the user onto
			// "Check & activate", so its step and stage may be a step behind (PREC-4490).
			(state, { payload }) => {
				if (state.state) {
					state.state.listsAwaitingGuide = payload.listsAwaitingGuide;
				}
			}
		);
		builder.addMatcher(
			onboardingTourApi.endpoints.completeOnboardingTour.matchFulfilled,
			(state, { payload }) => {
				hydrate(state, payload);
				close(state);
			}
		);
	},
});

export const {
	openTour,
	closeTour,
	setSkipModalOpen,
	setFinishModalOpen,
	setActiveHowItWorksStage,
	setConnectFlowOpen,
	setKbVisited,
	setKbEdited,
	setKbHandoffActive,
	setNudgeOpen,
	openListTour,
	closeListTour,
	setCurrentStepLocal,
	markStepCompletedLocal,
} = onboardingTourSlice.actions;

export const onboardingTourReducer = onboardingTourSlice.reducer;

export const onboardingTourSelector = (state: RootState) => state[onboardingTourSlice.name];
export const selectTourState = (state: RootState) => onboardingTourSelector(state).state;
export const selectIsTourOpen = (state: RootState) => onboardingTourSelector(state).isOpen;
export const selectSkipModalOpen = (state: RootState) =>
	onboardingTourSelector(state).skipModalOpen;
export const selectFinishModalOpen = (state: RootState) =>
	onboardingTourSelector(state).finishModalOpen;
export const selectActiveHowItWorksStage = (state: RootState) =>
	onboardingTourSelector(state).activeHowItWorksStage;
export const selectConnectFlowOpen = (state: RootState) =>
	onboardingTourSelector(state).connectFlowOpen;
export const selectKbVisited = (state: RootState) => onboardingTourSelector(state).kbVisited;
export const selectKbEdited = (state: RootState) => onboardingTourSelector(state).kbEdited;
export const selectKbHandoffActive = (state: RootState) =>
	onboardingTourSelector(state).kbHandoffActive ?? false;
export const selectNudgeOpen = (state: RootState) =>
	onboardingTourSelector(state).nudgeOpen ?? false;
export const selectListTourOpen = (state: RootState) =>
	onboardingTourSelector(state).listTourOpen ?? false;
export const selectTourVariant = (state: RootState) =>
	onboardingTourSelector(state).state?.variant ?? null;
